import React, { useCallback, useEffect, useRef, useState } from "react";
import { RequestListDataTable } from "./request-list-data-table";
import type { TableApi } from "@procore/data-table";
import { RequestStore } from "@/stores/request-store.core";
import type { FindRequestsPaginatedQueryParams } from "@laborchart-modules/lc-core-api/dist/api/requests/find-requests";
import { ProjectTearsheetProvider } from "../tearsheets/project/project-tearsheet";
import {
   Banner,
   Box,
   Button,
   InfoBanner,
   SplitViewCard,
   ToolLandingPage,
   useI18nContext,
} from "@procore/core-react";
import "./style.css";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { useViewPreferenceContext } from "@/react/providers/permission-context-provider";
import {
   columnHeadersMap,
   convertDataTableSort,
   convertRequestDataTableFilters,
   defaultRequestListTableConfig,
   filterFieldMap,
   filterRendererMap,
   mapApiRequestToTableRequest,
} from "./request-list-helpers";
import {
   useGetCustomFields,
   useGetGroupOptionsQuery,
   useGetSavedView,
} from "../common/queries/queries";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import { RequestTearsheetProvider } from "../tearsheets/request/request-tearsheet";
import type { ListServerFilter } from "../data-table/data-table-utils";
import { useGetAssignmentCreationSupportDataQuery } from "../tearsheets/assignment/queries";
import type { SortModel } from "@/react/prop-types";
import { FileDownloadToastProvider } from "../data-table/DataExport/FileDownload";
import { convertSavedViewToDataTableConfig, getDataForGrouping } from "@/react/shared/helper";
import type { ColumnDefinition } from "@procore/data-table";
import { ReportType } from "@/react/prop-types";
import type { SavedView } from "@laborchart-modules/common";
import { router } from "@/lib/router";

export function RequestListContainer({ setTitle }: { setTitle: (title: string) => void }) {
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const { getViewPreference } = useViewPreferenceContext();
   const isLastNameFirst = getViewPreference()?.displayLastNamesFirst() ?? false;
   const { data: customFields } = useGetCustomFields(CustomFieldEntity.REQUEST);
   const { data: assignmentCreationSupportData } =
      useGetAssignmentCreationSupportDataQuery(groupId);
   const { data: groupOptions } = useGetGroupOptionsQuery();
   const [tableApi, setTableApi] = useState<TableApi>();
   const [convertedSavedView, setConvertedSavedView] = React.useState<any>();

   const handleTableReady = (api: TableApi) => setTableApi(api);

   const groupedDataCacheRef = useRef<Array<Record<string, any>> | null>(null);

   const url = new URL(window.location.href);
   const params = url.searchParams;
   const [viewId] = useState(params.get("viewId"));

   const { data: savedViewData } = useGetSavedView(viewId);
   const [isLegacyBannerOpen, setIsLegacyBannerOpen] = useState(!!viewId);

   /* istanbul ignore next */
   useEffect(() => {
      if (viewId && savedViewData && customFields && !convertedSavedView) {
         const convertedSavedViewData = convertSavedViewToDataTableConfig(
            savedViewData?.data as SavedView,
            columnHeadersMap,
            defaultRequestListTableConfig,
            filterFieldMap,
            filterRendererMap,
            customFields,
         );
         setConvertedSavedView(convertedSavedViewData);
         setTitle(savedViewData.data?.name ?? I18n.t("views.company.workforce_planning.list"));
      }
   }, [
      groupId,
      viewId,
      savedViewData,
      convertedSavedView,
      setConvertedSavedView,
      customFields,
      setTitle,
   ]);

   const fetchRequestList = useCallback(
      async (
         startingAfter?: string,
         serverFilter?: ListServerFilter[],
         search?: string,
         sortModel?: SortModel[],
         groupKeys?: string[],
         rowGroupCols?: ColumnDefinition[],
      ) => {
         const isGrouped = rowGroupCols && rowGroupCols.length > 0;
         const isChildGroupRequest = isGrouped && groupKeys && groupKeys.length > 0;

         // For child-group requests, reuse the cached full dataset instead of re-fetching
         if (isChildGroupRequest && groupedDataCacheRef.current) {
            const filteredData = await getDataForGrouping(
               groupedDataCacheRef.current,
               rowGroupCols,
               groupKeys,
               ReportType.RequestList,
            );
            return {
               data: filteredData,
               pagination: { total_possible: filteredData.length },
            };
         }

         const coreApiFilters = convertRequestDataTableFilters(serverFilter ?? []);
         const { sort_by, direction } = convertDataTableSort(sortModel ?? []);
         const params: FindRequestsPaginatedQueryParams = {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            limit: 100,
            starting_after: startingAfter,
            group_id: groupId === "my-groups" ? undefined : groupId,
            filters: coreApiFilters,
            search,
            sort_by,
            sort_direction: direction,
         };
         const requestData = await RequestStore.findCombinedRequestsPaginated(params).payload;
         let requestListDetails = mapApiRequestToTableRequest(
            requestData.data,
            isLastNameFirst,
            assignmentCreationSupportData,
         );

         if (isGrouped) {
            if (requestData.pagination?.next_starting_after) {
               let nextStartingAfter: string | null = requestData.pagination.next_starting_after;
               do {
                  try {
                     const nextData: any = await RequestStore.findCombinedRequestsPaginated({
                        ...params,
                        starting_after: nextStartingAfter,
                        limit: 100,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                     }).payload;

                     const nextRequestListDetails = mapApiRequestToTableRequest(
                        nextData.data,
                        isLastNameFirst,
                        assignmentCreationSupportData,
                     );

                     requestListDetails = requestListDetails.concat(nextRequestListDetails);
                     nextStartingAfter = nextData.pagination?.next_starting_after;
                  } catch (error) {
                     console.error("Error fetching request list:", error);
                     break;
                  }
               } while (nextStartingAfter);
            }

            // Cache the full dataset for subsequent child-group requests
            groupedDataCacheRef.current = requestListDetails;

            requestListDetails = await getDataForGrouping(
               requestListDetails,
               rowGroupCols,
               groupKeys,
               ReportType.RequestList,
            );
            return {
               data: requestListDetails,
               pagination: { total_possible: requestListDetails.length },
            };
         }

         // Clear the grouped data cache when not grouping
         groupedDataCacheRef.current = null;

         return { data: requestListDetails, pagination: requestData.pagination };
      },
      [groupId, isLastNameFirst, assignmentCreationSupportData],
   );

   return (
      <RequestTearsheetProvider requestTableApi={tableApi}>
         <ProjectTearsheetProvider projectsTableApi={tableApi}>
            <FileDownloadToastProvider>
               <ToolLandingPage
                  className={"border-box request-list-detail-page data-table-list-detail-page"}
               >
                  <ToolLandingPage.Main>
                     <ToolLandingPage.Body>
                        {isLegacyBannerOpen && (
                           <InfoBanner>
                              <Banner.Content>
                                 <Banner.Title>
                                    {I18n.t(
                                       "views.company.workforce_planning.request.list_page_legacy_banner_title",
                                    )}
                                 </Banner.Title>
                                 <Banner.Body>
                                    {I18n.t(
                                       "views.company.workforce_planning.request.list_page_legacy_banner_description",
                                    )}
                                 </Banner.Body>
                              </Banner.Content>
                              <Banner.Action
                                 onClick={() =>
                                    router.navigate(
                                       null,
                                       `/groups/${groupId}/requests?viewId=${viewId}`,
                                    )
                                 }
                              >
                                 <Button>
                                    {I18n.t(
                                       "views.company.workforce_planning.request.list_page_legacy_banner_action",
                                    )}
                                 </Button>
                              </Banner.Action>
                              <Banner.Dismiss onClick={() => setIsLegacyBannerOpen(false)} />
                           </InfoBanner>
                        )}
                        <SplitViewCard className="request-list-card">
                           <SplitViewCard.Section className="request-list-section data-table-list-section">
                              <Box
                                 style={{
                                    marginTop: "5px",
                                    height: "100%",
                                 }}
                              >
                                 {groupOptions &&
                                    customFields &&
                                    (!viewId || convertedSavedView) && (
                                       <RequestListDataTable
                                          fetchRequestList={fetchRequestList}
                                          tableApi={tableApi}
                                          handleTableReady={handleTableReady}
                                          groupId={groupId}
                                          customFields={customFields}
                                          isLastNameFirst={isLastNameFirst}
                                          savedView={viewId ? convertedSavedView : null}
                                          groupOptions={groupOptions ?? []}
                                          assignmentCreationSupportData={
                                             assignmentCreationSupportData
                                          }
                                       />
                                    )}
                              </Box>
                           </SplitViewCard.Section>
                        </SplitViewCard>
                     </ToolLandingPage.Body>
                  </ToolLandingPage.Main>
               </ToolLandingPage>
            </FileDownloadToastProvider>
         </ProjectTearsheetProvider>
      </RequestTearsheetProvider>
   );
}
